/**
 * TelePortal hero types
 */

import { BaseEntity, TenantScoped } from '../core/common';
import type { TeleportalTaskStatus } from '../business/talent-drawer-vocabularies';

export type TelePortalTrackType = 'legal' | 'travel' | 'setup';

/**
 * Every value `teleportal_tracks.status` holds — THE declaration;
 * `@tonnie/database` types the column with it. The previous copy here missed
 * `archived`, which a placeholder track superseded by a generated kit holds.
 */
export const TELEPORTAL_TRACK_STATUSES = [
  'not_started',
  'in_progress',
  'completed',
  'blocked',
  'archived',
] as const;
export type TrackStatus = (typeof TELEPORTAL_TRACK_STATUSES)[number];

/**
 * A TelePortal task's status: exactly what `teleportal_tasks.status` holds —
 * the database is the truth, and `@tonnie/database` types that column with
 * this same declaration (`TELEPORTAL_TASK_STATUSES`).
 *
 * It used to be a hand-written copy that listed `waiting_validation` (never
 * stored by anything) and missed `archived` (stored for every pruned task), so
 * an exhaustive switch over it demanded a branch for a value the API can never
 * return and had none for one it does.
 */
export type TaskStatus = TeleportalTaskStatus;

/**
 * Every value `teleportal_documents.validation_status` can hold — the CLOSED
 * set, and the single source of truth for it.
 *
 * `processing` used to be listed here and is deliberately gone: it is the
 * status of the *agent extraction job* returned by `agentUpload`/`agentProcess`,
 * never a persisted validation status. Its presence made this union disagree
 * with the column's own `$type<TeleportalDocValidationStatus>` in
 * `@tonnie/database` and with the five-key locale node, so an exhaustive map
 * over it demanded a key for a value the DB can never produce.
 */
export const DOCUMENT_VALIDATION_STATUSES = [
  'pending',
  'uploaded',
  'validated',
  'rejected',
  'missing',
] as const;

export type DocumentValidationStatus = (typeof DOCUMENT_VALIDATION_STATUSES)[number];

/**
 * Task duration vocabulary — the CLOSED set of `estimated_duration` values the
 * brain catalog may emit and `teleportal_tasks.estimated_duration` may hold.
 *
 * WHY A UNION, AND WHY HERE
 * -------------------------
 * The value is a taxonomy, not free text: it is authored in the brain's
 * `task-templates.json` and rendered through `teleportal.duration.<slug>` keys
 * in five locales. Nothing tied the two together, so the two producers drifted:
 * the catalog carries 12 values, production carries a 13th (`3-5 days`, on 25
 * tasks) that no locale has a key for. Every consumer masked it with a
 * `t(key, '') || raw` fallback, so the miss rendered as English prose in every
 * locale instead of a raw key — invisible to the raw-key sweep.
 *
 * The union closes both ends: the brain catalog validator rejects a value that
 * is not here (boot + `npm run qa:teleportal-catalog`), and platform-api's zod
 * boundary for an incoming relocation kit rejects it too. The keys below are
 * DERIVED from the union, so adding a member fails the locale coverage spec
 * until the key exists in `en.json`.
 */
export const TELEPORTAL_TASK_DURATIONS = [
  '1 day',
  '1-2 days',
  '1-3 days',
  '3-5 days',
  '1 week',
  '1-2 weeks',
  '2-3 weeks',
  '2-4 weeks',
  '2-6 weeks',
  '2-8 weeks',
  '4-8 weeks',
  '4-12 weeks',
  'ongoing',
] as const;

export type TeleportalTaskDuration = (typeof TELEPORTAL_TASK_DURATIONS)[number];

/** i18n namespace the duration vocabulary is rendered from. */
export const TELEPORTAL_DURATION_KEY_PREFIX = 'teleportal.duration';

/**
 * `'1-2 weeks'` → `'1_2_weeks'`. THE normalizer — it used to be copied
 * verbatim into business-web, journey-web and citizen-mobile.
 *
 * Takes `string` rather than the union on purpose: it is applied to a value
 * read back from a `varchar` column, which may predate the union. An unknown
 * value yields a slug with no key, so `t()` renders the raw key and the i18n
 * sweep can see it — which is the point.
 */
export function teleportalDurationSlug(duration: string): string {
  return duration
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** `'1-2 weeks'` → `'teleportal.duration.1_2_weeks'`. Never returns a fallback. */
export function teleportalDurationKey(duration: string): string {
  return `${TELEPORTAL_DURATION_KEY_PREFIX}.${teleportalDurationSlug(duration)}`;
}

/**
 * The full key set, DERIVED from the union — the locale coverage spec asserts
 * `en.json` has every one of these, so a new duration fails the spec, not the
 * screen.
 */
export const TELEPORTAL_DURATION_KEYS: Record<TeleportalTaskDuration, string> = Object.fromEntries(
  TELEPORTAL_TASK_DURATIONS.map((d) => [d, teleportalDurationKey(d)])
) as Record<TeleportalTaskDuration, string>;

/** A TelePortal task's priority — the task templates' and the column's values. */
export const TASK_PRIORITIES = ['high', 'medium', 'low'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

export type HouseholdScope = 'talent_only' | 'household' | 'per_member' | 'per_child' | 'per_pet';

export type OwnershipMode = 'talent' | 'company' | 'government' | 'dual';

export type HouseholdMemberType = 'talent' | 'partner' | 'child' | 'pet' | 'extended_family';

export type ComplexityLevel = 'simple' | 'standard' | 'complex';

export type SchoolStage = 'pre_school' | 'primary' | 'secondary' | 'higher';

export type PetSpecies = 'dog' | 'cat' | 'other';

export type DependencyStatus = 'dependent' | 'independent';

/**
 * Package type
 */
export type PackageType = 'visa_application' | 'work_permit' | 'residence' | 'custom';

/**
 * Package status
 */
export type PackageStatus = 'generating' | 'completed' | 'expired';

/**
 * TelePortal track entity
 */
export interface TelePortalTrack extends BaseEntity, TenantScoped {
  journeyInstanceId: string;
  trackType: TelePortalTrackType;
  status: TrackStatus;
  progressPercent: number;
  startedAt?: Date | string | null;
  completedAt?: Date | string | null;
  isFoundation: boolean;
  icon?: string | null;
  color?: string | null;
}

/**
 * TelePortal task entity
 */
export interface TelePortalTask extends BaseEntity, TenantScoped {
  trackId: string;
  talentId: string;

  taskType: string;
  title: string;
  description?: string | null;
  instructions?: TaskInstruction[] | null;

  status: TaskStatus;
  priority: TaskPriority;
  estimatedDuration?: string | null;

  isBlocking: boolean;
  householdScope: HouseholdScope;
  dependsOn?: string[] | null;

  milesReward: number;
  sortOrder: number;

  dueDate?: Date | string | null;
  completedAt?: Date | string | null;

  ownershipMode: OwnershipMode;
  isCompanyTask: boolean;
  householdMemberId?: string | null;
  householdMember?: TelePortalHouseholdMember | null;
  assignedTo?: string | null;
  notes?: string | null;
  milestoneId?: string | null;
  templateId?: string | null;
}

/**
 * Task instruction
 */
export interface TaskInstruction {
  step: number;
  text: string;
  imageUrl?: string;
  linkUrl?: string;
  linkText?: string;
}

/**
 * TelePortal document entity
 */
export interface TelePortalDocument extends BaseEntity, TenantScoped {
  journeyInstanceId: string;
  trackId?: string | null;
  taskId?: string | null;
  vaultFileId?: string | null;

  documentType: string;
  documentName: string;
  description?: string | null;

  validationStatus: DocumentValidationStatus;
  tonnieChecked: boolean;
  validatedAt?: Date | string | null;
  validationNotes?: string | null;

  agentClassifiedType?: string | null;
  agentConfidence?: number | null;

  fileSize?: number | null;
  mimeType?: string | null;
  expiryDate?: Date | string | null;

  uploadedAt: Date | string;
  uploadedBy: string;

  householdMemberId?: string | null;
  isVaultLinked: boolean;
  warnings?: string[] | null;
}

/**
 * TelePortal application package entity
 */
export interface TelePortalApplicationPackage extends BaseEntity, TenantScoped {
  journeyInstanceId: string;
  name: string;
  description?: string | null;
  packageType: PackageType;
  status: PackageStatus;
  documentIds: string[];
  shareUrl?: string | null;
  shareAccessCode?: string | null;
  expiresAt?: Date | string | null;
  viewCount: number;
  createdBy: string;
}

/**
 * TelePortal progress summary
 */
export interface TelePortalProgress {
  legal: TrackProgress;
  travel: TrackProgress;
  setup: TrackProgress;
  overallPercent: number;
}

/**
 * Track progress
 */
export interface TrackProgress {
  status: TrackStatus;
  progressPercent: number;
  totalTasks: number;
  completedTasks: number;
  blockedTasks: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// SETUP PROFILE
// ═══════════════════════════════════════════════════════════════════════════

export interface TelePortalSetupProfile extends BaseEntity, TenantScoped {
  journeyInstanceId: string;
  setupVersion: number;
  completedAt?: Date | string | null;
  refreshedAt?: Date | string | null;
  refreshCount: number;
  complexityScore?: number | null;
  complexityLevel?: ComplexityLevel | null;
  dimensions: SetupDimensions;
  aiInsights?: Record<string, unknown> | null;
}

export interface SetupDimensions {
  household: HouseholdDimension;
  legalStatus: LegalStatusDimension;
  housing: HousingDimension;
  travel: TravelDimension;
  professional: ProfessionalDimension;
  vaultScan: VaultScanDimension;
  timeline: TimelineDimension;
  specialCircumstances: SpecialCircumstancesDimension;
}

export interface HouseholdDimension {
  members: Array<{
    type: HouseholdMemberType;
    name: string;
    nationality?: string;
    dateOfBirth?: string;
    schoolStage?: SchoolStage;
    profession?: string;
    petSpecies?: PetSpecies;
    petBreed?: string;
    relationship?: string;
    dependencyStatus?: DependencyStatus;
  }>;
  has_partner: boolean;
  has_children: boolean;
  has_pets: boolean;
}

export interface LegalStatusDimension {
  visaType?: string;
  currentStatus?: string;
  hasWorkPermit?: boolean;
  sponsorshipType?: string;
  /**
   * Structured destination visa pathway/category (e.g. `d1`, `d7`, `work_permit`,
   * `tech_visa`, `eu_family`), sourced from the destination country's `visa_types`
   * catalog. Drives pathway-specific obligations — including the company-provided
   * documents the sponsor must produce — via the Brain condition engine
   * (`legalStatus.visaPathway`). Distinct from the broad `situation` answer.
   */
  visaPathway?: string;
}

export interface HousingDimension {
  plan?: 'rent' | 'buy' | 'company_provided' | 'temporary';
  budgetRange?: string;
  preferredArea?: string;
}

export interface TravelDimension {
  arrivalMode?: 'one_way' | 'round_trip' | 'already_there';
  plannedArrivalDate?: string;
  hasPetsToTransport?: boolean;
}

export interface ProfessionalDimension {
  sponsorshipType?: string;
  needsCredentialRecognition?: boolean;
  currentEmployer?: string;
  jobTitle?: string;
  contractType?: string;
}

export interface VaultScanDimension {
  linkedDocuments?: string[];
  outdatedDocuments?: string[];
  skippedDocuments?: string[];
}

export interface TimelineDimension {
  urgency?: 'immediate' | 'within_3_months' | 'within_6_months' | 'flexible';
  targetDate?: string;
}

export interface SpecialCircumstancesDimension {
  items?: string[];
  freeText?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// HOUSEHOLD MEMBERS
// ═══════════════════════════════════════════════════════════════════════════

export interface TelePortalHouseholdMember extends BaseEntity, TenantScoped {
  journeyInstanceId: string;
  type: HouseholdMemberType;
  name: string;
  nationality?: string | null;
  dateOfBirth?: string | null;
  schoolStage?: SchoolStage | null;
  profession?: string | null;
  petSpecies?: PetSpecies | null;
  petBreed?: string | null;
  relationship?: string | null;
  dependencyStatus?: DependencyStatus | null;
  orderIndex: number;
  isActive: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// MILESTONES
// ═══════════════════════════════════════════════════════════════════════════

export interface TelePortalMilestone extends BaseEntity, TenantScoped {
  journeyInstanceId: string;
  track: string;
  templateId?: string | null;
  title: string;
  targetDate?: string | null;
  completedAt?: Date | string | null;
  tasksRequired: number;
  tasksCompleted: number;
  orderIndex: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// RELOCATION KIT (instantiation output)
// ═══════════════════════════════════════════════════════════════════════════

export interface RelocationKit {
  tracks: Array<{
    track_code: string;
    track_name_key: string;
    is_foundation: boolean;
    order_index: number;
    icon: string;
    color: string;
  }>;
  tasks: Array<{
    template_id: string;
    track: string;
    task_code: string;
    task_name_key: string;
    description_key: string;
    task_type: string;
    household_scope: string;
    ownership_mode: string;
    is_company_task: boolean;
    priority: string;
    is_blocking: boolean;
    is_required: boolean;
    requires_document: boolean;
    required_document_types: string[];
    estimated_duration: string;
    miles_reward: number;
    order_index: number;
    depends_on: string[];
    household_member_id: string | null;
    household_member_name: string | null;
    household_member_type: string | null;
  }>;
  milestones: Array<{
    template_id: string;
    track: string;
    title_key: string;
    order_index: number;
    tasks_required: number;
    required_task_codes: string[];
  }>;
  dependency_graph: Array<{ from: string; to: string }>;
  critical_path: string[];
  complexity: {
    score: number;
    level: ComplexityLevel;
    factors: string[];
  };
  metadata: {
    origin: string;
    destination: string;
    total_tasks: number;
    total_documents_required: number;
    household_member_count: number;
    generated_at: string;
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// DOCUMENT VISION (Brain.Actions output)
// ═══════════════════════════════════════════════════════════════════════════

export interface DocumentVisionResult {
  classification: {
    type: string;
    confidence: number;
  };
  extractedData: Record<string, string>;
  validation: {
    isValid: boolean;
    issues: string[];
    completeness: number;
  };
  quality: {
    score: number;
    issues: string[];
  };
  suggestions?: string[];
}
